// utils/retryUtils.js

/**
 * Retry configuration
 */
const DEFAULT_RETRY_CONFIG = {
  maxRetries: 3,
  baseDelay: 1000, // 1 second
  maxDelay: 10000, // 10 seconds
  retryableStatuses: [503, 502, 504, 429], // Service Unavailable, Bad Gateway, Gateway Timeout, Too Many Requests
  backoffMultiplier: 2
};

/**
 * Sleep utility
 * @param {number} ms - Milliseconds to sleep
 */
const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));

/**
 * Calculate delay with exponential backoff
 * @param {number} attempt - Current attempt number
 * @param {number} baseDelay - Base delay in ms
 * @param {number} maxDelay - Maximum delay in ms
 * @param {number} multiplier - Backoff multiplier
 */
const calculateDelay = (attempt, baseDelay, maxDelay, multiplier) => {
  const delay = baseDelay * Math.pow(multiplier, attempt - 1);
  return Math.min(delay, maxDelay);
};

/**
 * Determines if an error/response should be retried
 * @param {Response|Error} error - Response or error object
 * @param {Array} retryableStatuses - HTTP status codes that should be retried
 */
const shouldRetry = (error, retryableStatuses) => {
  // Handle fetch errors (network issues)
  if (error instanceof Error) {
    return error.name === 'TypeError' && error.message.includes('fetch');
  }
  
  // Handle HTTP response errors
  if (error && error.status) {
    return retryableStatuses.includes(error.status);
  }
  
  return false;
};

/**
 * Retry wrapper for async functions with exponential backoff
 * @param {Function} fn - Async function to retry
 * @param {Object} config - Retry configuration
 */
export const withRetry = async (fn, config = {}) => {
  const {
    maxRetries,
    baseDelay,
    maxDelay,
    retryableStatuses,
    backoffMultiplier
  } = { ...DEFAULT_RETRY_CONFIG, ...config };

  let lastError;
  
  for (let attempt = 1; attempt <= maxRetries + 1; attempt++) {
    try {
      const result = await fn();
      
      // If result is a Response object, check if it should be retried
      if (result && typeof result.status === 'number') {
        if (shouldRetry(result, retryableStatuses) && attempt <= maxRetries) {
          lastError = result;
          const delay = calculateDelay(attempt, baseDelay, maxDelay, backoffMultiplier);
          console.warn(`🔄 Request failed with status ${result.status}, retrying in ${delay}ms (attempt ${attempt}/${maxRetries})`);
          await sleep(delay);
          continue;
        }
      }
      
      return result;
    } catch (error) {
      lastError = error;
      
      if (shouldRetry(error, retryableStatuses) && attempt <= maxRetries) {
        const delay = calculateDelay(attempt, baseDelay, maxDelay, backoffMultiplier);
        console.warn(`🔄 Request failed, retrying in ${delay}ms (attempt ${attempt}/${maxRetries}):`, error.message);
        await sleep(delay);
        continue;
      }
      
      // If we've exhausted retries or it's not retryable, throw the error
      throw error;
    }
  }
  
  // This should never be reached, but just in case
  throw lastError;
};

/**
 * Specialized retry wrapper for auth operations
 * @param {Function} authFn - Auth function to retry
 */
export const withAuthRetry = async (authFn) => {
  return withRetry(authFn, {
    maxRetries: 2, // Less aggressive for auth
    baseDelay: 2000, // 2 second base delay
    retryableStatuses: [503, 502, 504] // Don't retry 429 for auth to prevent lockouts
  });
};

/**
 * Creates a circuit breaker pattern for repeated failures
 */
export class CircuitBreaker {
  constructor(config = {}) {
    this.failureThreshold = config.failureThreshold || 5;
    this.resetTimeout = config.resetTimeout || 60000; // 1 minute
    this.monitoringPeriod = config.monitoringPeriod || 300000; // 5 minutes
    
    this.state = 'CLOSED'; // CLOSED, OPEN, HALF_OPEN
    this.failureCount = 0;
    this.lastFailureTime = null;
    this.failures = [];
  }
  
  async execute(fn) {
    if (this.state === 'OPEN') {
      if (Date.now() - this.lastFailureTime > this.resetTimeout) {
        this.state = 'HALF_OPEN';
        this.failureCount = 0;
      } else {
        throw new Error('Circuit breaker is OPEN - service temporarily unavailable');
      }
    }
    
    try {
      const result = await fn();
      
      if (this.state === 'HALF_OPEN') {
        this.state = 'CLOSED';
        this.failureCount = 0;
        this.failures = [];
      }
      
      return result;
    } catch (error) {
      this.recordFailure();
      throw error;
    }
  }
  
  recordFailure() {
    const now = Date.now();
    this.failures.push(now);
    this.lastFailureTime = now;
    
    // Clean old failures outside monitoring period
    this.failures = this.failures.filter(time => now - time < this.monitoringPeriod);
    
    this.failureCount = this.failures.length;
    
    if (this.failureCount >= this.failureThreshold) {
      this.state = 'OPEN';
      console.warn(`🚨 Circuit breaker opened due to ${this.failureCount} failures`);
    }
  }
}

// Export a default circuit breaker for auth operations
export const authCircuitBreaker = new CircuitBreaker({
  failureThreshold: 3,
  resetTimeout: 30000, // 30 seconds
  monitoringPeriod: 120000 // 2 minutes
});